import React, { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import { FileSpreadsheet, FileText, Filter, MoreVertical, Search } from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import Pagination from "../../helpers/Pagination";
import SelectDropdown from "../../customStyles/SelectDropdown";
import CustomDatePicker from "../../customStyles/CustomDatePicker";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ExportCard from '../../helpers/ExportCard';
import { useRef } from "react";
import Logo from "../../assets/images/Logo.png";
import { useSelector } from "react-redux";
import {
  visitTypeOptions,
  visitSubType,
} from "../../components/ClientVisitsComponents/IndustryData";

const exportFollowupPDF = (data, logo) => {
  const doc = new jsPDF('l', 'mm', 'a4');

  const pageWidth = doc.internal.pageSize.getWidth();

  // ✅ LOGO
  if (logo) {
    doc.addImage(logo, "PNG", 10, 5, 20, 15);
  }

  // ✅ TITLE
  doc.setFontSize(16);
  doc.text("Follow-up Report", pageWidth / 2, 12, { align: "center" });

  // ✅ DATE
  doc.setFontSize(9);
  doc.text(
    `Generated on: ${format(new Date(), "dd-MM-yyyy HH:mm")}`,
    pageWidth / 2,
    18,
    { align: "center" }
  );

  // ✅ SUMMARY
  doc.setFontSize(9);
  doc.text(
    `Total Records: ${data.length}`,
    pageWidth - 10,
    12,
    { align: "right" }
  );

  // ✅ TABLE
  const tableData = data.map((item, index) => [
    index + 1,
    `${item.userName || "-"} (${item.userCode || "-"})`,
    `${item.clientName || "-"} (${item.clientId || "-"})`,
    item.visitType || "-",
    item.time || "-",
    item.remarks || "-",
    item.followUp?.split("T")[0] || "-"
  ]);

  autoTable(doc, {
    head: [[
      "S.No",
      "User",
      "Client",
      "Type",
      "Time",
      "Remarks",
      "FollowUp"
    ]],
    body: tableData,
    startY: 25,

    styles: {
      fontSize: 8,
      overflow: 'linebreak', // keep readable
    },

    headStyles: {
      fillColor: [220, 38, 38], // 🔴 your theme
      textColor: [255, 255, 255]
    },

    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      1: { cellWidth: 20 }, // User column
      2: { cellWidth: 40 }  // Client column
    }
  });

  // ✅ FOOTER
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 5,
      { align: "center" }
    );
  }

  // ✅ SAVE
  doc.save(`followup_report_${format(new Date(), "dd-MM-yyyy_HHmm")}.pdf`);
};

const exportFollowupExcel = (data) => {
  const sheetData = data.map((item, index) => ({
    "S.No": index + 1,
    User: `${item.userName} (${item.userCode})`,
    Client: `${item.clientName} (${item.clientId})`,
    Type: item.visitType,
    Time: item.time,
    Remarks: item.remarks,
    FollowUp: item.followUp?.split("T")[0] || "-"
  }));

  const ws = XLSX.utils.json_to_sheet(sheetData);
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "Followup Report");

  XLSX.writeFile(wb, "followup-report.xlsx");
};

const UserFollowupReport = () => {
  const today = format(new Date(), "yyyy-MM-dd");
  const navigate = useNavigate();
  const exportRef = useRef(null);
  const [filters, setFilters] = useState({
    fromDate: today,
    toDate: today,
    userId: ""
  });
  const user = useSelector((state) => state?.user?.user);
  const isOwner = user?.role === "owner" || user?.role === "admin";
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const [showFilters, setShowFilters] = useState(true);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [userOptions, setUserOptions] = useState([]); 

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [exportCard, setExportCard] = useState({
    isOpen: false,
    type: null,
    isLoading: false,
    exportProgress: 0,
    status: 'idle',
    message: '',
    autoClose: true,
    autoCloseDelay: 3000
  });

const handleExport = (type) => {
  setIsMoreOpen(false);

  setExportCard({
    isOpen: true,
    type,
    isLoading: true,
    exportProgress: 0,
    status: "loading",
    message: `Preparing ${type.toUpperCase()}...`
  });

  // simulate loading
  setTimeout(() => {
    setExportCard(prev => ({
      ...prev,
      isLoading: false,
      exportProgress: 100,
      status: "success",
      message: "File is ready to download"
    }));
  }, 1000);
};

  // useQuerySync(filters, searchTerm, true);
  const fetchUsers = useCallback(async () => {
    try {
      const res = await api({
        url: SummaryApi.getAllUsers.url,
        method: SummaryApi.getAllUsers.method,
      });

      if (res.data.success) {
        const users = res.data.data || [];

        const formatted = users.map(u => ({
          value: u._id,
          label: `${u.firstName || ""} ${u.lastName || ""} (${u.userId || ""})`
        }));

        setUserOptions(formatted);
      }
    } catch (err) {
      console.error("User fetch error", err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchReport = async (customFilters = filters) => {
    try {
      const params = new URLSearchParams();

      if (customFilters.fromDate) params.append("startDate", customFilters.fromDate);
      if (customFilters.toDate) params.append("endDate", customFilters.toDate);
      if (customFilters.userId) params.append("userId", customFilters.userId);

      const res = await api({
        url: `${SummaryApi.userWiseFollowupReport.url}?${params.toString()}`,
        method: SummaryApi.userWiseFollowupReport.method,
      });

      if (res.data.success) {
        setData(res.data.data.list);
        setStats(res.data.data.stats);
      }
    } catch (err) {
      console.error(err);
    }
  };

useEffect(() => {
  fetchReport(filters);

  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  navigate(`?${params.toString()}`, { replace: true });

// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // ✅ SEARCH FILTER
  useEffect(() => {
    let result = [...data];

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.clientName?.toLowerCase().includes(s) ||
        item.userName?.toLowerCase().includes(s)
      );
    }

    // if (filters.userId) {
    //   result = result.filter(item => item.userId === filters.userId);
    // }

    setFilteredData(result);
    setCurrentPage(1);
  }, [data, searchTerm]);

  // ✅ ACTIVE FILTER COUNT
  useEffect(() => {
    let count = 0;
    if (filters.fromDate || filters.toDate) count++;
    if (filters.userId) count++;
    if (searchTerm) count++;
    setActiveFiltersCount(count);
  }, [filters, searchTerm]);

  // pagination
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getVisitType = (item) => {
  const type =
    visitTypeOptions.find(v => v.id === item.visitType)?.label || item.visitType;

  const subType =
    visitSubType.find(v => v.id === item.visitSubType)?.label;

  return subType ? `${type} (${subType})` : type;
};
 const handleBackClick = () => {
    navigate(-1);
  };
  return (
    <div className="w-full p-4">

      {/* 🔴 HEADER */}
<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">

  {/* LEFT SIDE */}
  <div className="flex items-center gap-2">
    <button
      onClick={handleBackClick}
      className="hover:bg-gray-200 rounded-full p-2"
    >
      <FaArrowLeft className="w-5 h-5" />
    </button>

    <div>
      <h1 className="text-lg md:text-2xl font-bold">
        Follow-up Report
      </h1>
    </div>
  </div>

  {/* RIGHT SIDE */}
  <div className="flex items-stretch gap-2 w-full md:w-auto">

    {/* FILTER BUTTON */}
    <div className="relative flex justify-end sm:justify-start">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className={`p-2 rounded-lg ${
          showFilters ? "bg-red-100 text-red-700" : "bg-gray-100"
        }`}
      >
        <Filter className="w-5 h-5" />
      </button>

      {/* BADGE */}
      {activeFiltersCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] flex items-center justify-center bg-red-500 text-white rounded-full">
          {activeFiltersCount}
        </span>
      )}
    </div>

    {/* SEARCH */}
    <div className="relative w-full sm:w-64">
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
      />
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
    </div>
    <div className="relative " ref={exportRef}>
      <button
        onClick={() => setIsMoreOpen(!isMoreOpen)}
        className="p-2 rounded-md text-red-700 bg-red-100 hover:bg-gray-200"
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {isMoreOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white shadow-lg rounded-md z-10">
          
          <button
            onClick={() => isOwner && handleExport("excel")}
            disabled={!isOwner}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
              isOwner
                ? "hover:bg-gray-100 cursor-pointer"
                : "text-gray-400 cursor-not-allowed opacity-60"
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </button>

          <button
            onClick={() => isOwner && handleExport("pdf")}
            disabled={!isOwner}
            title={!isOwner ? "Only Admin can export" : ""}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
              isOwner
                ? "hover:bg-gray-100 cursor-pointer"
                : "text-gray-400 cursor-not-allowed opacity-60"
            }`}
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </button>

        </div>
      )}
    </div>
      </div>
    </div>

      {/* 🔴 FILTER SECTION */}
      {showFilters && (
        <div className="bg-white p-4 rounded shadow mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">

          <CustomDatePicker
            value={filters.fromDate}
            onChange={(d) =>
              setFilters(prev => {
                let newFrom = d;
                let newTo = prev.toDate;

                if (newTo && newFrom > newTo) {
                  newTo = newFrom;
                }

                return {
                  ...prev,
                  fromDate: newFrom,
                  toDate: newTo
                };
              })
            }
          />

          <CustomDatePicker
            value={filters.toDate}
            onChange={(d) =>
              setFilters(prev => {
                if (prev.fromDate && d < prev.fromDate) {
                  return prev; // block
                }

                return {
                  ...prev,
                  toDate: d
                };
              })
            }
          />

          <SelectDropdown
            options={userOptions}
            value={filters.userId}
            onChange={(v) => setFilters(prev => ({ ...prev, userId: v }))}
            placeholder="All Users"
            valueKey="value"
            labelKey="label"

          />

          {/* ✅ APPLY BUTTON FIXED */}
        <button
         onClick={() => {
          fetchReport(filters);

          const params = new URLSearchParams();

          if (searchTerm) params.set("search", searchTerm);

          Object.entries(filters).forEach(([key, value]) => {
            if (value) params.set(key, value);
          });

          navigate(`?${params.toString()}`);
        }}
          className="bg-red-500 text-white rounded-md px-4 py-[8px]"
        >
          Apply
        </button>
        </div>
      )}

      {/* 🔴 STATS */}
      {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-red-100 p-3 text-center rounded">
          <p>Total</p>
          <p className="font-bold">{stats.totalCalls || 0}</p>
        </div>
        <div className="bg-green-100 p-3 text-center rounded">
          <p>InPerson</p>
          <p>{stats.inPerson || 0}</p>
        </div>
        <div className="bg-blue-100 p-3 text-center rounded">
          <p>InBound</p>
          <p>{stats.inBound || 0}</p>
        </div>
        <div className="bg-purple-100 p-3 text-center rounded">
          <p>OutBound</p>
          <p>{stats.outBound || 0}</p>
        </div>
      </div> */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">

          {/* Total */}
        <div className="bg-red-100 p-4 rounded">
          <p className="text-sm font-semibold">Total</p>

          <h2 className="text-2xl font-bold">
            {stats.totalCalls || 0}
          </h2>

          <div className="mt-2 border-t pt-2 text-xs space-y-1">
            <div className="flex justify-between">
              <span>New Lead</span>
              <span className="font-semibold">{stats.initial || 0}</span>
            </div>

            <div className="flex justify-between">
              <span>Follow-Up</span>
              <span className="font-semibold">{stats.followUp || 0}</span>
            </div>
          </div>
        </div>

          {/* In-Person */}
          <div className="bg-green-100 p-4 rounded">
            <p className="text-sm font-semibold">In-Person</p>

            <h2 className="text-2xl font-bold">{stats.inPerson || 0}</h2>

            <div className="mt-2 text-xs space-y-1">
              <div className="flex justify-between">
                <span>New Lead</span>
                <span>{stats.inPersonInitial || 0}</span>
              </div>

              <div className="flex justify-between">
                <span>Follow-Up</span>
                <span>{stats.inPersonFollowUp || 0}</span>
              </div>
            </div>
          </div>

          {/* In-Bound */}
          <div className="bg-blue-100 p-4 rounded">
            <p className="text-sm font-semibold">In-Bound</p>

            <h2 className="text-2xl font-bold">{stats.inBound || 0}</h2>

            <div className="mt-2 text-xs space-y-1">
              <div className="flex justify-between">
                <span>New Lead</span>
                <span>{stats.inBoundInitial || 0}</span>
              </div>

              <div className="flex justify-between">
                <span>Follow-Up</span>
                <span>{stats.inBoundFollowUp || 0}</span>
              </div>
            </div>
          </div>

  {/* Out-Bound */}
  <div className="bg-purple-100 p-4 rounded">
    <p className="text-sm font-semibold">Out-Bound</p>

    <h2 className="text-2xl font-bold">{stats.outBound || 0}</h2>

    <div className="mt-2 text-xs space-y-1">
      <div className="flex justify-between">
        <span>New Lead</span>
        <span>{stats.outBoundInitial || 0}</span>
      </div>

      <div className="flex justify-between">
        <span>Follow-Up</span>
        <span>{stats.outBoundFollowUp || 0}</span>
      </div>
    </div>
  </div>

</div>

      {/* 🔴 TABLE */}
    <div className="overflow-x-auto rounded-md">
      <table className="min-w-full bg-white border-separate border-spacing-y-2 px-2">

        {/* 🔴 HEADER */}
        <thead className="text-sm text-gray-600">
          <tr className="text-center rounded-lg shadow-sm bg-red-100">
            <th className="py-4 px-4 rounded-l-lg">S.No</th>
            <th className="py-4 px-4">User</th>
            <th className="py-4 px-4">Client</th>
            <th className="py-4 px-4">Type</th>
            <th className="py-4 px-4">Time</th>
            <th className="py-4 px-4">Remarks</th>
            <th className="py-4 px-4 rounded-r-lg">FollowUp</th>
          </tr>
        </thead>

        {/* 🔴 BODY */}
        <tbody>
          {paginatedData.map((item, index) => (
            <tr
              key={index}
              className="bg-gray-50 hover:bg-gray-100 text-sm rounded-lg shadow-sm text-center"
            >

              {/* S.NO */}
              <td className="py-3 px-4 font-medium text-gray-700 rounded-l-lg">
                {currentPage * itemsPerPage - itemsPerPage + index + 1}
              </td>

              {/* USER */}
              <td className="py-3 px-4">
                <div className="min-w-[120px]">
                  <div className="font-medium text-gray-800 truncate">
                    {item.userName}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {item.userCode}
                  </div>
                </div>
              </td>

              {/* CLIENT */}
              <td className="py-3 px-4">
                <div className="min-w-[140px]">
                  <div className="font-semibold truncate">
                    {item.clientName}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {item.clientId}
                  </div>
                </div>
              </td>

              {/* TYPE */}
              <td className="py-3 px-4">
                {/* <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                  {item.visitType}
                </span> */}

                <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                  {getVisitType(item)}
                </span>
              </td>

              {/* TIME */}
              <td className="py-3 px-4 font-medium">
                {item.time}
              </td>

              {/* REMARKS */}
              <td className="py-3 px-4 text-left">
                <div
                  onClick={() => {
                    setViewData(item);
                    setIsDrawerOpen(true);
                  }}
                  className="max-w-[200px] truncate cursor-pointer text-blue-600 hover:underline"
                >
                  {item.remarks}
                </div>
              </td>

              {/* FOLLOWUP */}
              <td className="py-3 px-4 rounded-r-lg">
                <span className="text-red-500 font-medium">
                  {item.followUp?.split("T")[0] || "-"}
                </span>
              </td>

            </tr>
          ))}
        </tbody>

      </table>
    </div>

      {/* 🔴 PAGINATION */}
      {filteredData.length > 0 && (
        <Pagination
          totalItems={filteredData.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}

      <ExportCard
        isOpen={exportCard.isOpen}
        onClose={() =>
          setExportCard({
            isOpen: false,
            type: null,
            isLoading: false,
            exportProgress: 0,
            status: "idle",
            message: ""
          })
        }
        onExport={() => {
          if (exportCard.type === "excel") {
            exportFollowupExcel(filteredData);
          } else {
            exportFollowupPDF(filteredData, Logo);
          }
        }}
        isLoading={exportCard.isLoading}
        exportProgress={exportCard.exportProgress}
        status={exportCard.status}
        message={exportCard.message}
        type={exportCard.type}
      />
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 top-16 md:top-20 rounded-l-xl">

          {/* 🔴 Overlay */}
          <div
            className="absolute inset-0 transition-opacity"
            onClick={() => setIsDrawerOpen(false)}
          />

          {/* 🔴 Drawer */}
          <div className="absolute right-0 top-0 pb-20 h-screen w-full sm:w-[420px] bg-white shadow-2xl flex flex-col">

            {/* HEADER */}
            <div className="flex items-center justify-between px-5 py-4 border-b bg-red-50 shrink-0">
              <h2 className="text-lg font-semibold text-red-700">
                Follow-up Details
              </h2>

              <button
                onClick={() => setIsDrawerOpen(false)}
                className="text-gray-500 hover:text-red-600 text-lg"
              >
                ✕
              </button>
            </div>

            {/* ✅ SCROLL AREA */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">

              {viewData && (
                <>
                  {/* USER */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">User</p>
                    <p className="font-semibold text-gray-800">
                      {viewData.userName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {viewData.userCode}
                    </p>
                  </div>

                  {/* CLIENT */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Client</p>
                    <p className="font-semibold text-gray-800">
                      {viewData.clientName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {viewData.clientId}
                    </p>
                  </div>

                  {/* TYPE + TIME */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Type</p>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                        {viewData.visitType}
                      </span>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Time</p>
                      <p className="font-medium">{viewData.time}</p>
                    </div>
                  </div>

                  {/* FOLLOWUP */}
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <p className="text-xs text-red-500 mb-1">FollowUp Date</p>
                    <p className="font-semibold text-red-600">
                      {viewData.followUp?.split("T")[0] || "-"}
                    </p>
                  </div>

                  {/* REMARKS */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Remarks</p>
                    <div className="bg-gray-100 p-3 rounded-lg whitespace-pre-wrap">
                      {viewData.remarks}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFollowupReport;